import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SessionFilter } from '../api/discoveryAgent';
import { mergeObjectIdSelectedFilterMapFromOptions } from '../components/explore/utils/buildSessionFilters';

const DISCOVERY_PANEL_STORAGE_KEY = 'discovery-panel-state';

const defaultChatPanelWidth = 280;
const defaultInsightsPanelWidth = 600;

const CHAT_PANEL_MIN = 220;
const CHAT_PANEL_MAX = 340;
const INSIGHTS_PANEL_MIN = 400;
const INSIGHTS_RAIL_WIDTH = 40;

export { CHAT_PANEL_MIN, CHAT_PANEL_MAX, INSIGHTS_PANEL_MIN, INSIGHTS_RAIL_WIDTH };

export type IdeasListUiState = {
  page: number;
  selectedIdeaId?: string;
};

export type DiscoveryStore = {
  chatPanelWidth: number;
  selectedExploreTab: string;
  insightsPanelWidth: number;
  isInsightsPanelOpen: boolean;
  isSessionsOpen: boolean;
  isMessageSending: boolean;
  messageSendingSessionId: string | null;
  wasSessionsOpenBeforeInsights: boolean;
  activeMessageTimestamp: Date | null;
  inputValue: string;
  discoveryPromptCategoryKey: string | null;
  discoveryPromptCategoryHeading: string | null;
  isNewSession: boolean;
  optimisticUserMessage: string | null;
  pendingSessionId: string | null;
  activeEvidenceIds: string[];
  activeEvidenceLinkText: string;
  activeInsightSummary: string;
  artifactEvidenceIds: string[];
  activeArtifactId: string | null;
  activeArtifactRecordId: string | null;
  lastResolvedDeepLinkArtifactId: string | null;
  evidenceFilterBadgeText: string | null;
  /** When set true, Explore insights panel opens the create-idea modal (e.g. from artifact card). */
  pendingCreateIdeaModal: boolean;
  /** Artifact title that triggers CreateIdeaFromChatModal in ExploreMainContent. */
  pendingArtifactCreateIdeaTitle: string | null;
  /** Artifact _id sent with the create-idea payload from the Explore page. */
  pendingArtifactCreateIdeaArtifactId: string | null;
  ideasListUiByArtifactId: Record<string, IdeasListUiState>;
  sessionFilters: SessionFilter[];
  setOptimisticUserMessage: (value: string | null) => void;
  setActiveEvidenceIds: (ids: string[]) => void;
  setActiveArtifactId: (id: string | null) => void;
  setActiveArtifactRecordId: (id: string | null) => void;
  setLastResolvedDeepLinkArtifactId: (id: string | null) => void;
  setActiveEvidenceLinkText: (text: string) => void;
  setActiveInsightSummary: (summary: string) => void;
  setArtifactEvidenceIds: (ids: string[]) => void;
  setEvidenceFilterBadgeText: (text: string | null) => void;
  clearEvidenceFilter: () => void;
  setPendingCreateIdeaModal: (pending: boolean) => void;
  setPendingArtifactCreateIdeaTitle: (title: string | null) => void;
  setPendingArtifactCreateIdeaArtifactId: (id: string | null) => void;
  setIdeasListUiForArtifact: (stateKey: string, ui: IdeasListUiState) => void;
  setChatPanelWidth: (width: number) => void;
  setSelectedExploreTab: (tab: string) => void;
  setInsightsPanelWidth: (width: number) => void;
  setInsightsPanelOpen: (open: boolean) => void;
  setSessionsOpen: (open: boolean) => void;
  updateSendingSessionId: (sid: string) => void;
  startMessageSend: (forSessionId: string | null) => void;
  stopMessageSend: () => void;
  openInsights: () => void;
  closeInsights: () => void;
  setActiveMessageTimestamp: (timestamp: Date | null) => void;
  setInputValue: (value: string) => void;
  setDiscoveryPromptCategory: (categoryKey: string | null, heading: string | null) => void;
  setIsNewSession: (value: boolean) => void;
  setPendingSessionId: (id: string | null) => void;
  setSessionFilters: (filters: SessionFilter[]) => void;
  updateSessionFilter: (parent: string, selected: string[], type?: string, name?: string, options?: unknown) => void;
  getSelectedByParent: (parent: string) => string[];
  reset: () => void;
};

const sessionRelatedInitial = {
  activeMessageTimestamp: null as Date | null,
  inputValue: '',
  discoveryPromptCategoryKey: null as string | null,
  discoveryPromptCategoryHeading: null as string | null,
  isNewSession: false,
  optimisticUserMessage: null as string | null,
  pendingSessionId: null as string | null,
  activeEvidenceIds: [] as string[],
  activeEvidenceLinkText: '',
  activeInsightSummary: '',
  artifactEvidenceIds: [] as string[],
  activeArtifactId: null as string | null,
  activeArtifactRecordId: null as string | null,
  lastResolvedDeepLinkArtifactId: null as string | null,
  evidenceFilterBadgeText: null as string | null,
  pendingCreateIdeaModal: false,
  pendingArtifactCreateIdeaTitle: null as string | null,
  pendingArtifactCreateIdeaArtifactId: null as string | null,
  ideasListUiByArtifactId: {} as Record<string, IdeasListUiState>,
  sessionFilters: [] as SessionFilter[],
};

const persistedInitial = {
  chatPanelWidth: defaultChatPanelWidth,
  insightsPanelWidth: defaultInsightsPanelWidth,
  isInsightsPanelOpen: false,
  isSessionsOpen: true,
  wasSessionsOpenBeforeInsights: false,
  selectedExploreTab: 'discovery'
};

const initialState = {
  ...sessionRelatedInitial,
  ...persistedInitial,
  isMessageSending: false,
  messageSendingSessionId: null as string | null,
};

export const useDiscoveryStore = create<DiscoveryStore>()(
  persist(
    (set) => ({
      ...initialState,

      setChatPanelWidth: (width) => set({ chatPanelWidth: width }),

      setSelectedExploreTab: (tab) => set({ selectedExploreTab: tab }),

      setInsightsPanelWidth: (width) => set({ insightsPanelWidth: width }),

      setInsightsPanelOpen: (open) => set({ isInsightsPanelOpen: open }),

      updateSendingSessionId: (sid) => set({ messageSendingSessionId: sid }),

      startMessageSend: (forSessionId) =>
        set({ isMessageSending: true, messageSendingSessionId: forSessionId }),

      stopMessageSend: () => set({ isMessageSending: false, messageSendingSessionId: null }),

      setSessionsOpen: (open) =>
        set((state) => ({
          isSessionsOpen: open,
          ...(open && state.isInsightsPanelOpen && {
            isInsightsPanelOpen: false,
            wasSessionsOpenBeforeInsights: false,
          }),
        })),

      openInsights: () =>
        set((state) => ({
          wasSessionsOpenBeforeInsights: state.isSessionsOpen,
          isSessionsOpen: false,
          isInsightsPanelOpen: true,
        })),

      closeInsights: () =>
        set((state) => ({
          isInsightsPanelOpen: false,
          isSessionsOpen: state.wasSessionsOpenBeforeInsights,
          wasSessionsOpenBeforeInsights: false,
          activeEvidenceIds: [],
          activeMessageTimestamp: null,
          activeInsightSummary: '',
          artifactEvidenceIds: [],
          activeArtifactId: null,
          activeArtifactRecordId: null,
          evidenceFilterBadgeText: null,
          pendingCreateIdeaModal: false,
          pendingArtifactCreateIdeaTitle: null,
          pendingArtifactCreateIdeaArtifactId: null,
        })),

      setActiveMessageTimestamp: (timestamp) =>
        set({ activeMessageTimestamp: timestamp }),

      setInputValue: (value) => set({ inputValue: value }),

      setDiscoveryPromptCategory: (categoryKey, heading) =>
        set({
          discoveryPromptCategoryKey: categoryKey,
          discoveryPromptCategoryHeading: heading,
        }),

      setIsNewSession: (value) => set({ isNewSession: value }),

      setPendingSessionId: (id) => set({ pendingSessionId: id }),

      setOptimisticUserMessage: (value) => set({ optimisticUserMessage: value }),

      setActiveEvidenceIds: (ids) => set({ activeEvidenceIds: ids }),

      setActiveEvidenceLinkText: (text) => set({ activeEvidenceLinkText: text }),

      setActiveInsightSummary: (summary) => set({ activeInsightSummary: summary }),

      setArtifactEvidenceIds: (ids) => set({ artifactEvidenceIds: ids }),

      setActiveArtifactId: (id) => set({ activeArtifactId: id }),

      setActiveArtifactRecordId: (id) => set({ activeArtifactRecordId: id }),

      setLastResolvedDeepLinkArtifactId: (id) => set({ lastResolvedDeepLinkArtifactId: id }),

      setEvidenceFilterBadgeText: (text) => set({ evidenceFilterBadgeText: text }),

      clearEvidenceFilter: () =>
        set((state) => ({
          activeEvidenceIds: state.artifactEvidenceIds,
          evidenceFilterBadgeText: null,
        })),
      setPendingCreateIdeaModal: (pending) => set({ pendingCreateIdeaModal: pending }),
      setPendingArtifactCreateIdeaTitle: (title) => set({ pendingArtifactCreateIdeaTitle: title }),
      setPendingArtifactCreateIdeaArtifactId: (id) => set({ pendingArtifactCreateIdeaArtifactId: id }),

      setIdeasListUiForArtifact: (stateKey, ui) =>
        set((state) => ({
          ideasListUiByArtifactId: {
            ...state.ideasListUiByArtifactId,
            [stateKey]: ui,
          },
        })),

      setSessionFilters: (filters) => set({ sessionFilters: filters }),

      updateSessionFilter: (parent: string, selected: string[], type = 'checkbox', name?: string, options?: any) =>
        set((state) => {
          if (selected.length === 0) {
            return {
              sessionFilters: state.sessionFilters.filter((f) => f.parent !== parent),
            };
          }
          const existing = state.sessionFilters.find((f) => f.parent === parent);
          const base: SessionFilter = existing
            ? { ...existing, selected, ...(name !== undefined && { name }) }
            : { parent, type, selected, filterObject: 'evidence', ...(name !== undefined && { name }) };
          const entry =
            options !== undefined
              ? mergeObjectIdSelectedFilterMapFromOptions(base, options)
              : base;
          const next = existing
            ? state.sessionFilters.map((f) => (f.parent === parent ? entry : f))
            : [...state.sessionFilters.filter((f) => f.parent !== parent), entry];
          return { sessionFilters: next };
        }),

      getSelectedByParent: (parent) => {
        const state: DiscoveryStore = useDiscoveryStore.getState();
        const f = state.sessionFilters.find((x) => x.parent === parent);
        return (f?.selected ?? []).map(String);
      },

      reset: () =>
        set({
          ...sessionRelatedInitial,
          pendingCreateIdeaModal: false,
          pendingArtifactCreateIdeaTitle: null,
          pendingArtifactCreateIdeaArtifactId: null,
          ideasListUiByArtifactId: {},
          sessionFilters: [],
          isInsightsPanelOpen: false,
          isSessionsOpen: true,
          wasSessionsOpenBeforeInsights: false,
          isMessageSending: false,
          messageSendingSessionId: null,
        }),
    }),
    {
      name: DISCOVERY_PANEL_STORAGE_KEY,
      partialize: (state) => ({
        insightsPanelWidth: state.insightsPanelWidth,
        selectedExploreTab: state.selectedExploreTab
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<Pick<DiscoveryStore, 'insightsPanelWidth' | 'selectedExploreTab'>> | undefined;
        return {
          ...current,
          chatPanelWidth: persistedInitial.chatPanelWidth,
          selectedExploreTab: p?.selectedExploreTab ?? persistedInitial.selectedExploreTab,
          insightsPanelWidth: p?.insightsPanelWidth ?? persistedInitial.insightsPanelWidth,
          isInsightsPanelOpen: persistedInitial.isInsightsPanelOpen,
          isSessionsOpen: persistedInitial.isSessionsOpen,
          wasSessionsOpenBeforeInsights: persistedInitial.wasSessionsOpenBeforeInsights,
        };
      },
    }
  )
);
